/**
 * Every string `/content/new` renders.
 *
 * The lede carries the two facts the operator needs before they start: the type
 * decides the shape of everything below it, and **what they save is a draft
 * nobody sees**. The state is stated rather than offered, because a selector
 * with one legitimate value is not a choice.
 */
export const copy = {
  crumb: 'عنوان جديد',

  head: {
    title: 'عنوان جديد',
    ledeLead: 'النوع أوّلاً، لأنّه هو ما يحدّد بقيّة الحقول وشكل الأصول تحته. العنوان الجديد يُحفَظ',
    ledeStrong: 'مسوّدةً',
    ledeTail: 'ولا يراه أحد؛ النشر والجدولة يقعان بعدها من قائمة المحتوى.',
  },

  type: {
    heading: 'النوع',
    label: 'نوع المحتوى',
    notes: {
      film: 'الفيلم ملفٌّ واحد بلا مواسم. ترفعه من صفحة الأصول بعد حفظ العنوان.',
      series: 'المسلسل حلقاتٌ موزّعة على مواسم. تُنشئ المواسم وترفع حلقاتها من صفحة الأصول بعد حفظ العنوان.',
    } as Readonly<Record<string, string>>,
  },

  provider: {
    heading: 'التعبئة من المزوّد',
    lead: (provider: string) =>
      `المزوّد المرتبط ${provider}. ابحث بالعنوان أو بالمعرّف، ثمّ املأ الحقول بفعل واحد — وكلّ حقل يبقى قابلاً للتحرير بعد التعبئة.`,
    queryLabel: 'العنوان أو معرّف المزوّد',
    queryHint: 'المعرّف بالصيغة tt0000000، والبحث بالعنوان يقبل العربية واللاتينية معاً.',
    search: 'ابحث',
    searching: 'جارٍ البحث…',
    /**
     * The lookup is scoped to a title that exists, so searching saves the draft
     * first. Said once, before the press, rather than discovered afterwards.
     */
    savesDraftFirst: 'البحث يحفظ العنوان مسوّدةً أوّلاً كي لا تفقد ما كتبته.',
    needsTitle: 'اكتب العنوان أوّلاً، ثمّ ابحث.',
    resultsCaption: 'مطابقات المزوّد — اختر واحدة',
    noResults: 'لا مطابقة. راجع الكتابة، أو املأ الحقول بنفسك.',
    /** Said on the row, never used to drop it — the operator decides. */
    kindMismatch: 'نوع مختلف',
    apply: 'املأ الحقول من المطابقة',
    applying: 'جارٍ التعبئة…',
    applyNote: 'تملأ: السنة، وبلد الإنتاج، والتصنيف العمري، والتصنيف، والملخّص، والمنسوبين، والصور.',
    applied: (count: number) => `مُلئ ${count} حقلاً. راجعها وعدّل ما يلزم.`,
    referenceLead: 'الرابط المرجعيّ الذي سيظهر على صفحة العنوان',
    referenceText: (provider: string, externalId: string) => `الصفحة على ${provider} ${externalId}`,
  },

  cover: {
    heading: 'الغلاف',
    dropLabel: 'أفلت صورة الغلاف هنا',
    /**
     * **Both axes and the published ratio** — this line stated `min_width` and
     * a hardcoded `2:3`. `backend-assets` refuses a source that misses the
     * minimum on EITHER axis, so a 1920×1080 still clears the `1000` this hint
     * advertised and is still refused on the `1500` it never mentioned: the
     * hint certified a file the service cannot accept. The ratio is an
     * operator's setting on `ARTWORK_PURPOSES` besides, and a literal here
     * goes stale the moment it is changed.
     */
    hint: (formats: string, ratio: string, floor: string) =>
      ratio && floor
        ? `الصيغ المقبولة: ${formats}. أصغر مقاس مقبول ${floor} بكسل بنسبة ${ratio} للمصغّرة.`
        : `الصيغ المقبولة: ${formats}.`,
    uploading: 'جارٍ رفع الغلاف…',
    uploaded: 'رُفع الغلاف.',
    /** The crop worker writes the cover onto the title; the upload waits for it. */
    notDerived: 'رُفع الغلاف، لكنّه لم يظهر بعد. أعد المحاولة أو حدّث الصفحة لتتحقّق.',
  },

  checklist: {
    heading: 'قبل النشر',
    lede: 'المسوّدة تُحفَظ ناقصةً. هذه القائمة تقول ما يبقى قبل أن يصير العنوان قابلاً للنشر من قائمة المحتوى.',
    /** The label per `REQUIRED_METADATA` member — the membership is read, the wording is ours. */
    fields: {
      title: 'العنوان',
      description: 'الوصف',
      genre: 'التصنيف',
      cover: 'الغلاف',
      production_year: 'سنة الإنتاج',
      producing_country: 'بلد الإنتاج',
      age_rating: 'التصنيف العمري',
    } as Readonly<Record<string, string>>,
    ready: 'اكتمل المطلوب — يمكن نشره من قائمة المحتوى.',
    /** The figure arrives already rendered through the shared formatter. */
    remaining: (count: string) => `بقي ${count} من الحقول المطلوبة.`,
    present: 'مكتمل',
    missing: 'ناقص',
  },

  credits: {
    heading: 'المنسوبون',
    lede: 'اختياريّ. تملؤه التعبئة من المزوّد ويبقى قابلاً للتحرير.',
    /** Credits attach to a record, so the title is saved before they can. */
    needsDraft: 'احفظ العنوان أوّلاً، ثمّ أضف المنسوبين إليه.',
  },

  actions: {
    save: 'احفظ العنوان',
    saving: 'جارٍ الحفظ…',
    cancel: 'ألغِ',
  },

  done: {
    heading: 'حُفِظ العنوان',
    bodyLead: '«',
    bodyTail: '» الآن مسوّدة — لا يراه أحد حتى يُنشَر من قائمة المحتوى.',
    next: 'الخطوة التالية رفع الأصول: ملفّ الفيلم، أو حلقات المسلسل داخل مواسمها. لا يظهر العنوان للمشاهدين قبل أن يحمل ملفّاً واحداً على الأقلّ.',
    toAssets: 'أضف الأصول',
    toEdit: 'تابِع التحرير',
    toList: 'عد إلى القائمة',
  },

  states: {
    loading: 'جارٍ تحميل النموذج',
  },

  /**
   * One line per rule the form schema names, keyed by the schema's own code.
   *
   * Keyed by **code**, never matched on message text: the server's `message` is
   * Pydantic's English, and rendering it put «String should have at least 1
   * character» under an Arabic label on an Arabic-only surface.
   */
  leaveGuard: {
    heading: 'مغادرة بلا حفظ؟',
    body: 'لديك تغييرات غير محفوظة على هذا العنوان. إن غادرت الآن فلن تُحفظ.',
    commitLabel: 'غادر بلا حفظ',
    intent: 'warning',
  },

  validation: {
    required: 'هذا الحقل مطلوب.',
    yearShape: 'أدخل سنة من أربعة أرقام، مثل ٢٠٢٤.',
  },
} as const;
